import React, { useState, useEffect } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import TagInput from './components/TagInput';
import ArrayFieldCard from './components/ArrayFieldCard';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';
import ExperiencesInput from './components/ExperiencesInput';

const EnglishTeacherResumeForm = ({ resumeData, onUpdate, validationErrors = {}, onFieldFocus, onFieldBlur, getDynamicSections, renderDynamicSectionForm }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newCert, setNewCert] = useState('');
  const [newWebsite, setNewWebsite] = useState("");
  const [newTechnology, setNewTechnology] = useState("");
  const [newLanguage, setNewLanguage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const skillCategories = [
    { key: 'english_subjects', label: 'English Subjects' },
    { key: 'teaching_methods', label: 'Teaching Methods' },
    { key: 'literature_knowledge', label: 'Literature Knowledge' },
    { key: 'technology_tools', label: 'Technology Tools' },
    { key: 'programming_languages', label: 'Programming Languages' },
    { key: 'frameworks', label: 'Frameworks' },
    { key: 'tools', label: 'Tools' },
    { key: 'ai_ml_technologies', label: 'AI/ML Technologies' },
  ];

  const handleClearAllSkills = () => {
    onUpdate({
      ...resumeData,
      skills: {},
      key_skills: [],
    });
  };

  const addProject = () => {
    updateField("projects", [...(resumeData.projects || []), {
        title: "",
        name: "",
        description: "",
        link: "",
        year: "",
        technologies: []
    }]);
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };

  const removeWebsite = (index) => {
    const updatedWebsites = [...(resumeData.websites || [])];
    updatedWebsites.splice(index, 1);
    updateField("websites", updatedWebsites);
  };

  const addTechnologyToProject = (projectIndex) => {
    if (newTechnology.trim()) {
      const updatedProjects = [...(resumeData.projects || [])];
      updatedProjects[projectIndex].technologies = [
        ...(updatedProjects[projectIndex].technologies || []),
        newTechnology.trim()
      ];
      updateField("projects", updatedProjects);
      setNewTechnology("");
    }
  };

  const updateField = (field, data) => {
    onUpdate({ ...resumeData, [field]: data });
  };

  const handlePersonalChange = (field, value) => {
    onUpdate({ ...resumeData, [field]: value });
  };

  const addCert = () => {
    if (newCert.trim()) {
      updateField("certifications", [...(resumeData.certifications || []), newCert.trim()]);
      setNewCert("");
    }
  };

  const removeCert = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    if (newLanguage.trim()) {
      updateField("languages", [...(resumeData.languages || []), newLanguage.trim()]);
      setNewLanguage("");
    }
  };

  const removeLanguage = (index) => {
    updateField("languages", resumeData.languages.filter((_, i) => i !== index));
  };

  // Function to categorize skills from key_skills into structured format
  const categorizeSkills = (keySkills) => {
    if (!Array.isArray(keySkills)) return {};
    
    const categorized = {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };

    keySkills.forEach(skill => {
      const skillLower = skill.toLowerCase();
      if (skillLower.includes('python') || skillLower.includes('java') || skillLower.includes('javascript') || 
          skillLower.includes('c++') || skillLower.includes('sql') || skillLower.includes('html') || 
          skillLower.includes('css') || skillLower.includes('php') || skillLower.includes('ruby') ||
          skillLower.includes('go') || skillLower.includes('rust') || skillLower.includes('swift') ||
          skillLower.includes('c#') || skillLower.includes('kotlin') || skillLower.includes('scala')) {
        categorized.programming_languages.push(skill);
      } else if (skillLower.includes('react') || skillLower.includes('angular') || skillLower.includes('vue') ||
                 skillLower.includes('node') || skillLower.includes('django') || skillLower.includes('flask') ||
                 skillLower.includes('spring') || skillLower.includes('express') || skillLower.includes('laravel') ||
                 skillLower.includes('asp.net') || skillLower.includes('jquery') || skillLower.includes('bootstrap')) {
        categorized.frameworks.push(skill);
      } else if (skillLower.includes('git') || skillLower.includes('docker') || skillLower.includes('aws') ||
                 skillLower.includes('jenkins') || skillLower.includes('kubernetes') || skillLower.includes('jira') ||
                 skillLower.includes('agile') || skillLower.includes('scrum') || skillLower.includes('devops') ||
                 skillLower.includes('ci/cd') || skillLower.includes('terraform') || skillLower.includes('ansible')) {
        categorized.tools.push(skill);
      } else if (skillLower.includes('ai') || skillLower.includes('ml') || skillLower.includes('tensorflow') ||
                 skillLower.includes('pytorch') || skillLower.includes('scikit') || skillLower.includes('opencv') ||
                 skillLower.includes('deep learning') || skillLower.includes('neural networks') || skillLower.includes('nlp') ||
                 skillLower.includes('computer vision') || skillLower.includes('data science') || skillLower.includes('statistics')) {
        categorized.ai_ml_technologies.push(skill);
      } else {
        // For business/soft skills, add to tools category
        categorized.tools.push(skill);
      }
    });

    return categorized;
  };

  // Get skills data - prioritize structured skills, fallback to categorized key_skills
  // For templates, show empty skills. For uploaded resumes, show the skills data.
  const getSkillsData = () => {
    // If this is a template and no user data has been entered, return empty skills
    if (resumeData.isTemplate !== false && 
        (!resumeData.full_name || resumeData.full_name.trim() === '') &&
        (!resumeData.email || resumeData.email.trim() === '') &&
        (!resumeData.phone || resumeData.phone.trim() === '')) {
      return {
        programming_languages: [],
        frameworks: [],
        tools: [],
        ai_ml_technologies: []
      };
    }
    
    if (resumeData.skills && Object.keys(resumeData.skills).length > 0) {
      return resumeData.skills;
    }
    
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0) {
      return categorizeSkills(resumeData.key_skills);
    }
    
    return {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };
  };

  // Auto-convert key_skills to structured skills when component mounts or resumeData changes
  // Only convert if this is NOT a template (i.e., user has uploaded a resume)
  useEffect(() => {
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0 && 
        (!resumeData.skills || Object.keys(resumeData.skills).length === 0) &&
        resumeData.isTemplate === false) {
      const categorizedSkills = categorizeSkills(resumeData.key_skills);
      updateField('skills', categorizedSkills);
    }
  }, [resumeData.key_skills, resumeData.skills, resumeData.isTemplate]);

  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      location: "",
      graduation_date: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const updateProject = (index, key, value) => {
  const updated = [...resumeData.projects];
  updated[index][key] = value;
  
  // Maintain data consistency across templates
  if (key === 'technologies') {
    updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
  }
  if (key === 'tech_stack') {
    updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
  }
  if (key === 'link') {
    updated[index].github_link = value;
  }
  if (key === 'github_link') {
    updated[index].link = value;
  }
  
  onUpdate({ ...resumeData, projects: updated });
};

  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        handlePersonalChange('profile_image_url', e.target.result);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const baseSections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'projects', label: 'Projects' },
    { id: 'achievements', label: 'Achievements' }
  ];

  // Combine base sections with dynamic sections from ResumeEditor
  const sections = getDynamicSections ? [...baseSections, ...getDynamicSections()] : baseSections;

  return (
    <div className="resume-form" style={{ marginTop: 0, paddingTop: 0 }}>
      <div className="form-content" style={{ marginTop: 0, paddingTop: 0 }}>
        <div className="form-header">
          <h2 className="form-title">Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
  <div className="tab-panel">
    <h3 className="section-title">Contact Information</h3>
    <div className="form-group-grid">
      <div className={`form-group ${validationErrors.profile_image_url ? 'error' : ''}`}>
                <label>Profile Picture</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {resumeData?.profile_image_url && (
                    <img 
                      src={resumeData.profile_image_url} 
                      alt="Profile Preview" 
                      style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ddd' }}
                    />
                  )}
                  <div>
                    <input
                      type="file"
                      id="profile-image-upload"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                    <label htmlFor="profile-image-upload" className="add-button" style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.7 : 1 }}>
                      {isUploading ? 'Uploading...' : 'Upload Photo'}
                    </label>
                    {resumeData?.profile_image_url && (
                       <button 
                         type="button"
                         onClick={() => handlePersonalChange('profile_image_url', '')} 
                         className="remove-button" 
                         style={{marginLeft: '10px'}}
                       >
                         🗑️ Remove
                       </button>
                    )}
                  </div>
                </div>
                {validationErrors.profile_image_url && <p className="error-message">{validationErrors.profile_image_url}</p>}
              </div>

      <FormInput
        label="Full Name"
        name="full_name"
        value={resumeData?.full_name || ""}
        onChange={(value) => handlePersonalChange("full_name", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="Scarlett Jackson"
        maxLength={50}
        error={validationErrors.full_name}
      />

      <FormInput
        label="Job Title"
        name="job_title"
        value={resumeData?.job_title || ""}
        onChange={(value) => handlePersonalChange("job_title", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="High School English Teacher"
        maxLength={60}
        error={validationErrors.job_title}
      />

      <FormInput
        label="Email Address"
        name="email"
        type="email"
        value={resumeData?.email || ""}
        onChange={(value) => handlePersonalChange("email", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="support@resumedesign.ai"
        maxLength={60}
        error={validationErrors.email}
      />

      <FormInput
        label="Phone Number"
        name="phone"
        value={resumeData?.phone || ""}
        onChange={(value) => handlePersonalChange("phone", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="(555) 345-6789"
        maxLength={20}
        error={validationErrors.phone}
      />

      <FormInput
        label="Location"
        name="location"
        value={resumeData?.address || resumeData?.location || ""}
        onChange={(value) => {
          handlePersonalChange("address", value);
          handlePersonalChange("location", value);
        }}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="New York, US"
        maxLength={50}
        error={validationErrors.location}
      />

      <FormInput
        label="LinkedIn"
        name="linkedin"
        value={resumeData?.linkedin || ""}
        onChange={(value) => handlePersonalChange("linkedin", value)}
        placeholder="https://linkedin.com/in/yourprofile"
      />

      <FormInput
        label="GitHub"
        name="github"
        value={resumeData?.github || ""}
        onChange={(value) => handlePersonalChange("github", value)}
        placeholder="https://github.com/yourusername"
      />

      <FormInput
        label="LeetCode"
        name="leetcode"
        value={resumeData?.leetcode || ""}
        onChange={(value) => handlePersonalChange("leetcode", value)}
        placeholder="https://leetcode.com/yourprofile"
      />

      <FormInput
        label="HackerRank"
        name="hackerrank"
        value={resumeData?.hackerrank || ""}
        onChange={(value) => handlePersonalChange("hackerrank", value)}
        placeholder="https://hackerrank.com/yourprofile"
      />

    </div>

    <div className="space-y-2">
      <label className="text-sm font-medium">Languages</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={newLanguage}
          onChange={(e) => setNewLanguage(e.target.value)}
          placeholder="e.g., English, Spanish"
          maxLength={50}
          className="flex-1 placeholder:text-muted-foreground/60 placeholder:italic"
        />
        <AddButton onClick={addLanguage} variant="secondary" size="default">
          Add
        </AddButton>
      </div>
      {(resumeData.languages || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {(resumeData.languages || []).map((lang, idx) => (
            <span key={idx} className="inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground">
              {lang}
              <RemoveButton
                onClick={() => removeLanguage(idx)}
                ariaLabel={`Remove language ${idx + 1}`}
                size="icon"
                variant="ghost"
                className="h-4 w-4"
                useXIcon={true}
              />
            </span>
          ))}
        </div>
      )}
    </div>

    <FormInput
      label="Personal Website"
      name="personal_website"
      type="url"
      value={resumeData?.personal_website || ''}
      onChange={(value) => handlePersonalChange('personal_website', value)}
      placeholder="https://yourwebsite.com"
      maxLength={200}
    />

    <FormInput
      label="Portfolio"
      name="portfolio"
      type="url"
      value={resumeData?.portfolio || ''}
      onChange={(value) => handlePersonalChange('portfolio', value)}
      placeholder="https://portfolio.yourname.com"
      maxLength={200}
    />
  </div>
)}

          


          {activeSection === 'summary' && (
  <div className="tab-panel">
    <h3 className="section-title">Professional Summary</h3>
    <FormTextarea
      label="Professional Summary"
      name="professional_summary"
      value={resumeData?.professional_summary || ""}
      onChange={(value) => updateField("professional_summary", value)}
      onFocus={onFieldFocus}
      onBlur={onFieldBlur}
      rows={6}
      placeholder="e.g. Passionate and dedicated High School English Teacher with experience in creating engaging lesson plans and fostering a love for literature in students..."
      maxLength={1000}
      error={validationErrors.professional_summary}
    />
  </div>
)}


{activeSection === 'experience' && (
  <div className="tab-panel">
    <ExperiencesInput
      experiences={resumeData.relevant_experience}
      onChange={(updated) => updateField("relevant_experience", updated)}
      errors={validationErrors.relevant_experience}
      onFieldFocus={onFieldFocus}
      onFieldBlur={onFieldBlur}
      title="Teaching Experience"
      addLabel="Add Experience"
      companyLabel="School"
      positionPlaceholder="High School English Teacher"
      companyPlaceholder="ABC High School"
      locationPlaceholder="New York, NY"
      startDatePlaceholder="Aug 2008"
      endDatePlaceholder="Present"
      pointsPlaceholder="Describe your teaching responsibilities..."
      positionMaxLength={70}
      companyMaxLength={70}
      locationMaxLength={50}
      startDateMaxLength={20}
      endDateMaxLength={20}
      pointsMaxLength={300}
    />
  </div>
)}

{activeSection === 'education' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Education</h3>
      <AddButton onClick={addEducation}>Add Education</AddButton>
    </div>
    {(resumeData.education || []).map((edu, index) => {
      const eduErrors = validationErrors.education?.[index] || {};
      return (
        <ArrayFieldCard
          key={index}
          title={`Education ${index + 1}`}
          index={index}
          onRemove={() => removeEducation(index)}
        >
          <FormInput
            label="Degree"
            name={`edu-degree-${index}`}
            value={edu.degree || ""}
            onChange={(value) => updateEducation(index, "degree", value)}
            placeholder="Masters in Education"
          />

          <FormInput
            label="Field of Study"
            name={`edu-field-${index}`}
            value={edu.field_of_study || ""}
            onChange={(value) => updateEducation(index, "field_of_study", value)}
            placeholder="Education"
          />

          <FormInput
            label="Institution"
            name={`edu-institute-${index}`}
            value={edu.institute || ""}
            onChange={(value) => updateEducation(index, "institute", value)}
            placeholder="University of Education"
          />

          <FormInput
            label="Location"
            name={`edu-location-${index}`}
            value={edu.location || ""}
            onChange={(value) => updateEducation(index, "location", value)}
            placeholder="New York, NY"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Start Year"
              name={`edu-start-${index}`}
              value={edu.start_year || ""}
              onChange={(value) => updateEducation(index, "start_year", value)}
              placeholder="2009"
            />
            <FormInput
              label="End Year"
              name={`edu-end-${index}`}
              value={edu.end_year || ""}
              onChange={(value) => updateEducation(index, "end_year", value)}
              placeholder="2011"
            />
          </div>

          <FormTextarea
            label="Description"
            name={`edu-desc-${index}`}
            value={edu.description || ""}
            onChange={(value) => updateEducation(index, "description", value)}
            placeholder="Focused on advanced teaching techniques and curriculum development."
            rows={3}
          />
        </ArrayFieldCard>
      );
    })}
  </div>
)}


{activeSection === 'certifications' && (
  <div className="tab-panel">
    <h3 className="section-title">Certifications</h3>
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={newCert}
          onChange={(e) => setNewCert(e.target.value)}
          placeholder="Add a certification"
          maxLength={100}
          className="flex-1 placeholder:text-muted-foreground/60 placeholder:italic"
        />
        <AddButton onClick={addCert} variant="secondary" size="default">
          Add
        </AddButton>
      </div>
      {(resumeData.certifications || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {(resumeData.certifications || []).map((cert, index) => (
            <span key={index} className="inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground">
              <span>
                {typeof cert === 'string' ? cert : (cert.name || cert)}
                {typeof cert === 'object' && cert.provider ? ` – ${cert.provider}` : ""}
              </span>
              <RemoveButton
                onClick={() => removeCert(index)}
                ariaLabel={`Remove certification ${index + 1}`}
                size="icon"
                variant="ghost"
                className="h-4 w-4"
                useXIcon={true}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
)}


{activeSection === 'skills' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Skills</h3>
    </div>

    <CategorizedSkillsInput
      skills={resumeData.skills || {}}
      onClear={handleClearAllSkills}
      clearLabel="Clear"
      onChange={(updatedSkills) => {
        // Skills are now managed exclusively via structured `skills`.
        // Clear `key_skills` for compatibility if it exists.
        onUpdate({
          ...resumeData,
          skills: updatedSkills,
          ...(resumeData.key_skills ? { key_skills: [] } : {}),
        });
      }}
      categories={skillCategories.map(({ key, label }) => ({
        key,
        label,
        placeholder: `Add ${label}...`,
      }))}
    />
  </div>
)}


{activeSection === 'achievements' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Achievements</h3>
      <AddButton onClick={() => updateField("achievements", [...(resumeData.achievements || []), ""])}>
        Add Achievement
      </AddButton>
    </div>
    {(resumeData.achievements || []).map((item, index) => (
      <div key={index} className="flex gap-2 items-start mb-4">
        <FormInput
          label=""
          name={`achievement-${index}`}
          value={item}
          onChange={(value) => {
            const updated = [...resumeData.achievements];
            updated[index] = value;
            updateField("achievements", updated);
          }}
          placeholder="E.g., Improved standardized test scores by 20% through innovative teaching methods"
          className="flex-1"
        />
        <RemoveButton
          onClick={() => {
            const updated = resumeData.achievements.filter((_, i) => i !== index);
            updateField("achievements", updated);
          }}
          ariaLabel={`Remove achievement ${index + 1}`}
          size="icon"
          variant="ghost"
          className="mt-6"
        />
      </div>
    ))}
  </div>
)}


{activeSection === 'projects' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Projects/Activities</h3>
      <AddButton onClick={addProject}>Add Project</AddButton>
    </div>
    {(resumeData.projects || []).map((proj, index) => (
      <ArrayFieldCard
        key={index}
        title={`Project ${index + 1}`}
        index={index}
        onRemove={() => removeProject(index)}
      >
        <FormInput
          label="Title"
          name={`proj-title-${index}`}
          value={proj.title || ''}
          onChange={(value) => updateProject(index, "title", value)}
          placeholder="After-school Writing Program"
          maxLength={100}
        />
        <FormTextarea
          label="Description"
          name={`proj-desc-${index}`}
          value={proj.description || ''}
          onChange={(value) => updateProject(index, "description", value)}
          placeholder="A description of your project or activity."
          rows={3}
          maxLength={300}
        />
        <FormInput
          label="GitHub Link"
          name={`proj-github-${index}`}
          value={proj.github_link || proj.link || ''}
          onChange={(value) => updateProject(index, "github_link", value)}
          placeholder="https://github.com/your/project"
          maxLength={100}
        />
        <FormInput
          label="Year"
          name={`proj-year-${index}`}
          value={proj.year || ""}
          onChange={(value) => updateProject(index, "year", value)}
          placeholder="2023"
          maxLength={10}
        />
        <FormInput
          label="Technologies (comma separated)"
          name={`proj-tech-${index}`}
          value={Array.isArray(proj.technologies) ? proj.technologies.join(', ') : ''}
          onChange={(value) => {
            const technologies = value.split(',').map(t => t.trim()).filter(t => t);
            updateProject(index, "technologies", technologies);
          }}
          placeholder="Google Classroom, Zoom, Microsoft Office"
          maxLength={100}
        />
      </ArrayFieldCard>
    ))}
  </div>
)}

          {/* DYNAMIC SECTIONS - Rendered from ResumeEditor.jsx */}
          {renderDynamicSectionForm && getDynamicSections && getDynamicSections().map(section => {
            if (activeSection === section.id) {
              return renderDynamicSectionForm(section.id);
            }
            return null;
          })}
          
  
        </div>
      </div>
    </div>
  );
} 
 
export default EnglishTeacherResumeForm;
